import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, Tag, Image as ImageIcon } from "lucide-react";
import MicrositeTimeModal from "./MicrositeTimeModal";
import { Service } from "../../types/service";

interface ServiceModalProps {
  open: boolean;
  onClose: () => void;
  service: Service;
  themeColor:string
}
const ServiceModal: React.FC<ServiceModalProps> = ({ open, onClose, service,themeColor="#885ABB" }) => {

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Overlay */}
          <motion.div
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black backdrop-blur-sm"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35 }}
            className="fixed right-0 top-0 z-50 flex h-full w-full sm:w-[85%] md:w-[60%] lg:w-[50%] flex-col bg-white shadow-2xl rounded-l-2xl overflow-hidden font-serif"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute right-5 top-5 z-50 text-gray-400 hover:text-gray-800 transition"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Content */}
            <div className="p-6 flex-1 overflow-y-auto space-y-8 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="space-y-2 border-b pb-4"
              >
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 tracking-wide">
                  {service.title}
                </h2>
                <p className="text-sm md:text-base text-gray-500">
                  Experience luxury and comfort crafted exclusively for you.
                </p>
              </motion.div>

              {/* Price & Duration */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="grid grid-cols-2 gap-4 bg-gray-50 rounded-2xl px-4 py-3 border border-gray-100 shadow-sm"
              >
                {service.price && (
                  <div className="flex items-center gap-2 text-gray-800">
                    <Tag className="w-4 h-4" style={{ color: themeColor }} />
                    <span className="font-semibold">Price:</span>
                    <span className="text-gray-600">{service.price}</span>
                  </div>
                )}
                {service.duration && (
                  <div className="flex items-center gap-2 text-gray-800">
                    <Clock className="w-4 h-4" style={{ color: themeColor }} />
                    <span className="font-semibold">Duration:</span>
                    <span className="text-gray-600">{service.duration}</span>
                  </div>
                )}
              </motion.div>

              {/* Timings */}
              {service.timings && service.timings.length > 0 && (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: 0.2 }}
    className="space-y-4"
  >
    <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: themeColor }}>
      <Clock className="w-5 h-5" style={{ color: themeColor }} />
      Timings
    </h3>
    <p className="text-sm text-gray-500">Standard working hours.</p>
    <MicrositeTimeModal timings={service.timings} themeColor={themeColor} />
  </motion.div>
)}

              {/* Gallery */}
              {service.gallery && service.gallery.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: themeColor }}>
                    <ImageIcon className="w-5 h-5" style={{ color: themeColor }} />
                    Gallery
                  </h3>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {service.gallery.map((img, idx) => (
                      <motion.img
                        key={idx}
                        src={img}
                        alt={`Gallery ${idx + 1}`}
                        className="w-full h-28 md:h-24 object-cover rounded-lg cursor-pointer shadow-sm hover:shadow-lg transition-transform"
                       
                       
                      />
                    ))}
                  </div>

                 
                </motion.div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ServiceModal;
